/**
 * Where a body's drawn surface sits along one direction out of its centre -
 * the ray and shape-field readers cruise, the ground-contact law and the
 * walkaround air all read off - and the datum radii the anchor, the pitch
 * floor and the drawn leaves agree on.
 */

import * as THREE from 'three';

import { latLonToVec3 } from './latLonToVec3.ts';
import type { AtlasLive } from '../atlas';
import type { AtmosphereViewHandle } from './atmosphereView';
import type { BodyReadersHandle } from './bodyReaders';
import type { BodySceneHandle } from './bodyScene';
import type { CameraRigHandle } from './cameraRig';
import type { DojoPaintsHandle } from './dojoPaints';
import type { InjectsHandle } from './injects';
import type { PyramidViewHandle } from './pyramidView';
import {
  meshDrawnRadiusM, shapeFieldRadiusM, shapeHeightAt, shapeMeshMaxRadius, shapeMeshRadiusAt,
} from './shapeGlobe.ts';
import { atlasToBody } from './tileMesh.ts';
import type { OrreryBody } from './orrery';
import type { WorldHostState } from './worldHost';
import { isShapeBodyRow } from '../world/bodies.ts';
import type { WorldVec } from '../world/frame';
import { TEST_SWITCHES } from '../urlFlags.ts';

export interface SurfaceRadiusDeps {
  readonly live: AtlasLive;
  readonly injects: InjectsHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly worldHostState: WorldHostState;
  readonly bodyScene: BodySceneHandle;
  /** Built after this module: each arrives as a getter. */
  readonly cameraRig: CameraRigHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly dojoPaints: DojoPaintsHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly _v: THREE.Vector3;
}

export interface SurfaceRadiusHandle {
  drawnSurfaceRadiusM(id: string, dir: WorldVec): number | null;
  restingGroundRadiusM(id: string, dir: WorldVec): number | null;
  shapeSurfaceRadiusSimM(node: OrreryBody, x: number, y: number, z: number): number | null;
  shapeOuterRadiusM(id: string): number | null;
  reliefSurfaceRadiusM(id: string, dir: WorldVec): number | null;
  reliefOuterRadiusM(id: string): number | null;
  cruiseGroundUnderEyeM(
    node: OrreryBody, dx: number, dy: number, dz: number, r: number,
  ): number;
  datumRadiusM(id: string | null): number | null;
  groundDatumM(id: string | null): number;
  leafDatumM(): number;
  surfaceWorld(
    body: OrreryBody, latDeg: number, lonDeg: number, radius?: number,
  ): WorldVec;
  readonly GREY_GROUND_BOUNCE: boolean;
}

export function createSurfaceRadius(deps: SurfaceRadiusDeps): SurfaceRadiusHandle {
  const _surfRay = new THREE.Raycaster();
  const _surfDir = new THREE.Vector3();
  const _surfFrom = new THREE.Vector3();
  const _surfBack = new THREE.Vector3();
  const _surfHits: THREE.Intersection[] = [];
  const _surfQ = new THREE.Quaternion();
  /** `?groundmagnified=1`: the sim's ground reader back on the globe exactly as
   *  drawn, the reading before it waited for the shape.
   *  `ground-under-boot-check.mjs --inject magnified-sphere` reaches it. */
  const GROUND_AS_DRAWN = TEST_SWITCHES
    && new URLSearchParams(location.search).get('groundmagnified') === '1';
  /** `?sceneaxiseye=1`: `cruiseGroundUnderEyeM` back on the atlas subtree's own
   *  scene axes, the direction it handed the reader before this fix.
   *  `air-eye-frame-check.mjs --inject scene-axis-eye` reaches it. */
  const SCENE_AXIS_EYE = TEST_SWITCHES
    && new URLSearchParams(location.search).get('sceneaxiseye') === '1';

  /**
   * Where the drawn globe's surface is along one direction out of a body's
   * centre, metres, as the sim asks it: the ground a ship lands on.
   *
   * At the body's own size, so the angular floor's magnification of a far globe
   * comes back off. A shape-model body answers off its height field alone, and
   * null until that lands: its globe is the datum sphere until then, which is
   * not its ground.
   *
   * `dir` stays in the sim's own axes: the readers below resolve it through the
   * mesh's WORLD transform, and the Atlas subtree already carries the turn out
   * of Atlas axes, so `eclipticToScene` here would read the height a quarter
   * turn away - kilometres of relief on a body drawn as its own shape.
   */
  function drawnSurfaceRadiusM(id: string, dir: WorldVec): number | null {
    if (GROUND_AS_DRAWN) return drawnSurfaceRadiusSimM(id, dir.x, dir.y, dir.z);
    const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
    if (node === null) return null;
    if (isShapeBodyRow(node.row)) return shapeSurfaceRadiusSimM(node, dir.x, dir.y, dir.z);
    const drawn = drawnSurfaceRadiusSimM(id, dir.x, dir.y, dir.z);
    if (drawn === null) return null;
    return (drawn * node.drawRadiusM) / ((node.mesh.scale.x || 1) * node.row.radius_m);
  }

  const _restKey = new THREE.Vector3();
  let restBodyId: string | null = null;
  let restMesh: THREE.Mesh | null = null;
  let restGeom: THREE.BufferGeometry | null = null;
  let restField: unknown = null;
  let restRadiusM = 0;
  const restDir = new THREE.Vector3();

  /**
   * `drawnSurfaceRadiusM` for a ship resting on the body, the reader its seat
   * was put down with, so the pilot panel measures from the same ground.
   *
   * Cached on the direction in the mesh's own axes: a resting ship turns with
   * the body, so that direction holds and the ray against the globe's
   * 196,608 triangles runs once a landing rather than once a frame.
   */
  function restingGroundRadiusM(id: string, dir: WorldVec): number | null {
    const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
    if (node === null) return null;
    _restKey.set(dir.x, dir.y, dir.z);
    if (_restKey.lengthSq() === 0) return drawnSurfaceRadiusM(id, dir);
    node.mesh.updateWorldMatrix(true, false);
    _restKey.normalize().applyQuaternion(node.mesh.getWorldQuaternion(_surfQ).invert());
    const field = deps.bodyScene.state.shapeGlobes?.field(id) ?? null;
    if (restBodyId !== id || restMesh !== node.mesh || restGeom !== node.mesh.geometry
      || restField !== field || _restKey.dot(restDir) < 1 - 1e-12) {
      const r = drawnSurfaceRadiusM(id, dir);
      if (r === null) return null;
      restBodyId = id;
      restMesh = node.mesh;
      restGeom = node.mesh.geometry;
      restField = field;
      restRadiusM = r;
      restDir.copy(_restKey);
    }
    return restRadiusM;
  }

  /**
   * The same surface off the height field the globe's mesh was displaced by,
   * rather than off the mesh. Metres at the body's OWN size, so the angular
   * floor's magnification is no part of it. Null with no field resident.
   *
   * Preferred over the ray on a SHAPE body: there the L0 field is the whole of
   * the relief the mesh discretises, and the contact law asks per substep,
   * where a ray against 196,608 triangles is milliseconds and this is one
   * bilinear sample. Null on every other body even while its own L0 field is
   * resident - Mars's height floors at level 7 and Luna's at 7, so their L0 is
   * a coarse top the leaves draw finer, and reading it here would move the
   * seat, FLOAT and the sky's ground radius off the mesh those bodies are
   * drawn as (median 26 m on Mars, 114 m on Luna, up to 2.4 km).
   *
   * `x, y, z` stay in the sim's own ecliptic axes, same as `drawnSurfaceRadiusM`.
   */
  function shapeSurfaceRadiusSimM(
    node: OrreryBody, x: number, y: number, z: number,
  ): number | null {
    if (!isShapeBodyRow(node.row)) return null;
    const field = deps.bodyScene.state.shapeGlobes?.field(node.row.id) ?? null;
    if (field === null) return null;
    node.mesh.updateWorldMatrix(true, false);
    return shapeFieldRadiusM(node.drawRadiusM, field.datumRadiusM, (dir) => {
      _surfDir.set(...dir);
      _surfDir.applyQuaternion(node.mesh.getWorldQuaternion(_surfQ).invert());
      return shapeHeightAt(field, atlasToBody(_surfDir));
    }, x, y, z);
  }

  /**
   * `drawnSurfaceRadiusM` on a body drawn as a sphere with relief, read off the
   * one facet of the globe's displaced cubesphere the direction falls in
   * (`shapeMeshRadiusAt`) instead of a ray against every facet: the same
   * triangle the ray meets, at a plane intersection's cost, which is what lets
   * the contact law ask it per substep. Null on a shape-model body, which reads
   * its field above, and while the globe is still a plain sphere.
   */
  function reliefSurfaceRadiusM(id: string, dir: WorldVec): number | null {
    const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
    if (node === null || isShapeBodyRow(node.row)) return null;
    _surfDir.set(dir.x, dir.y, dir.z);
    if (_surfDir.lengthSq() === 0) return null;
    node.mesh.updateWorldMatrix(true, false);
    _surfDir.applyQuaternion(node.mesh.getWorldQuaternion(_surfQ).invert());
    const r = shapeMeshRadiusAt(node.mesh.geometry, atlasToBody(_surfDir));
    return r > 0 ? (r * node.drawRadiusM) / node.row.radius_m : null;
  }

  /** The radius nothing of that globe stands past, metres, or null alongside
   *  a `reliefSurfaceRadiusM` that has nothing to answer with. */
  function reliefOuterRadiusM(id: string): number | null {
    const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
    if (node === null || isShapeBodyRow(node.row)) return null;
    const r = shapeMeshMaxRadius(node.mesh.geometry);
    return r === null ? null : (r * node.drawRadiusM) / node.row.radius_m;
  }

  /** The radius nothing of a body's drawn shape stands past, metres, or null
   *  on a body whose datum sphere is the whole of it as far as contact is
   *  concerned - the same gate the reader above carries. */
  function shapeOuterRadiusM(id: string): number | null {
    const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
    const field = node === null || !isShapeBodyRow(node.row)
      ? null : deps.bodyScene.state.shapeGlobes?.field(id) ?? null;
    if (node === null || field === null) return null;
    return node.drawRadiusM * (1 + field.maxHeightM / field.datumRadiusM);
  }

  /**
   * The globe exactly as drawn, magnification and all. The ray comes in from
   * outside the mesh and takes its first hit, so a front-facing material is all
   * it needs; a miss falls back to the radius the body is drawn at.
   */
  function drawnSurfaceRadiusSimM(
    id: string, x: number, y: number, z: number,
  ): number | null {
    const node = deps.bodyScene.state.orrery?.byId(id) ?? null;
    if (node === null) return null;
    const scale = node.mesh.scale.x || 1;
    // The mesh's own undisplaced radius, not `drawRadiusM`: on a DTM body
    // (Mars) that is the pyramid's datum, which the geometry is not built at.
    const fallback = meshDrawnRadiusM(node.row, scale);
    _surfDir.set(x, y, z);
    if (_surfDir.lengthSq() === 0) return fallback;
    const shaped = shapeSurfaceRadiusSimM(node, x, y, z);
    // The ray reads the mesh as the angular floor magnifies it; the field is
    // the body's own size, so a magnified globe gets that factor put back.
    if (shaped !== null) return (shaped * scale * node.row.radius_m) / node.drawRadiusM;
    _surfDir.set(x, y, z);
    _surfDir.normalize();
    node.mesh.updateWorldMatrix(true, false);
    node.mesh.getWorldPosition(_surfFrom);
    const out = fallback * 4;
    _surfFrom.addScaledVector(_surfDir, out);
    _surfRay.set(_surfFrom, _surfBack.copy(_surfDir).negate());
    _surfRay.near = 0;
    _surfRay.far = out * 2;
    _surfHits.length = 0;
    node.mesh.raycast(_surfRay, _surfHits);
    if (_surfHits.length === 0) return fallback;
    let nearest = Infinity;
    for (const hit of _surfHits) if (hit.distance < nearest) nearest = hit.distance;
    _surfHits.length = 0;
    const r = out - nearest;
    return r > 0 ? r : fallback;
  }

  /**
   * How far the eye has to be over the datum before the relief under it stops
   * being worth a ray: the whole correction is a few kilometres and the sky's
   * horizon term only notices it near the ground.
   */
  const RELIEF_READ_RADII = 0.01;
  /** `?datumhorizon=1`: the air's eye height measured from the datum however
   *  low the eye is, which is the reading before this existed.
   *  `grounded-view-check.mjs --inject datum-horizon` reaches it. */
  const DATUM_HORIZON = TEST_SWITCHES
    && new URLSearchParams(location.search).get('datumhorizon') === '1';
  /** `?skyolive=1`: the sky's ground bounce back at one grey number, which is
   *  the reading before the ground's own colour reached it.
   *  `descent-dojo --skyheight --inject sky-olive` reaches it. */
  const GREY_GROUND_BOUNCE = TEST_SWITCHES
    && new URLSearchParams(location.search).get('skyolive') === '1';
  let reliefBodyId: string | null = null;
  let reliefDir: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
  let reliefRadiusM = 0;
  // The mesh the cached radius was read off. A held pose swings the direction
  // by nothing, so without these a shape globe that swaps its geometry for a
  // finer one keeps answering off the coarse mesh it no longer draws.
  let reliefMesh: THREE.Mesh | null = null;
  let reliefGeom: THREE.BufferGeometry | null = null;
  let reliefField: unknown = null;

  /**
   * The drawn ground's radius under the eye in the walkaround, metres, or null
   * wherever there is no mesh to read one off.
   *
   * The plot mesh IS the drawn ground at SURFACE, and its own clearance is what
   * the eye floor and `eyePose` already measure the eye against, so the air
   * reads the eye at the height the player is standing at rather than at the
   * datum a site kilometres below it never reaches.
   */
  function surfaceGroundRadiusM(r: number): number | null {
    if (!deps.cameraRig.surfaceRegime() || !deps.live.plot || !deps.live.plotField) return null;
    const g = r - deps.live.plot.clearanceAboveGround(deps.cameraRig.camPos);
    return g > 0 ? g : null;
  }

  /**
   * The ground radius the air's eye height is measured from: the plot mesh in
   * the walkaround, the drawn globe under the eye in every other regime, the
   * datum only where nothing is drawn.
   *
   * The body is drawn as one relief mesh, so an eye over Mars is standing over
   * ground that runs kilometres under `radius_m`, and the Solar System view's
   * bands are as far below that datum as cruise is. Measured from the datum the
   * eye reads as being ON the ground, the sky's horizon term puts the air's own
   * limb at the datum's tangent, and the band between that tangent and the
   * lower drawn limb comes back ground-terminated - black sky between the
   * ground and the haze. Measured from the ground actually under the eye the
   * two limbs land together, and the sky-view table is sampled at the radius
   * the eye is at rather than at the one a site kilometres below never reaches.
   *
   * Re-rayed when the eye has swung a fair way round the body, since the shape
   * mesh's own vertices are tens of kilometres apart, and whenever the mesh,
   * its geometry or the shape field under it is replaced.
   */
  function cruiseGroundUnderEyeM(
    node: OrreryBody, dx: number, dy: number, dz: number, r: number,
  ): number {
    if (DATUM_HORIZON || r <= 0) return deps.atmosphereView.atmoGroundM;
    if (!deps.worldHostState.externalWorld) {
      const g = surfaceGroundRadiusM(r);
      if (g !== null) return g;
      // `eye-on-datum` restores the datum HERE and nowhere else, which is
      // exactly what this returned before the relief read reached the bands
      // outside cruise: the walkaround keeps the plot mesh above and cruise
      // keeps its own ray below.
      if (deps.dojoPaints.skyDebug.eyeOnDatum) return deps.atmosphereView.atmoGroundM;
    }
    if (r - deps.atmosphereView.atmoGroundM > deps.atmosphereView.atmoGroundM * RELIEF_READ_RADII) return deps.atmosphereView.atmoGroundM;
    let ux: number;
    let uy: number;
    let uz: number;
    if (SCENE_AXIS_EYE) {
      ux = dx / r;
      uy = dy / r;
      uz = dz / r;
    } else {
      // Undoes the turn `eclipticToScene` (`atlas/orrery.ts`) put on `dx, dy, dz`:
      // `cameraWorld` and `node.world` are the atlas subtree's own scene axes
      // (this function's only caller, `atmoAltitudeM`, differences two of them),
      // and the readers below want the sim's own ecliptic axes, untouched, same
      // as `drawnSurfaceRadiusM`'s callers pass.
      ux = dx / r;
      uy = -dz / r;
      uz = dy / r;
    }
    const field = deps.bodyScene.state.shapeGlobes?.field(node.row.id) ?? null;
    if (reliefBodyId !== node.row.id
      || reliefMesh !== node.mesh || reliefGeom !== node.mesh.geometry || reliefField !== field
      || !(ux * reliefDir.x + uy * reliefDir.y + uz * reliefDir.z >= 0.999999)) {
      if (!Number.isFinite(ux + uy + uz)) return deps.atmosphereView.atmoGroundM;
      const hit = drawnSurfaceRadiusSimM(node.row.id, ux, uy, uz);
      if (hit === null) return deps.atmosphereView.atmoGroundM;
      reliefBodyId = node.row.id;
      reliefDir = { x: ux, y: uy, z: uz };
      reliefRadiusM = hit;
      reliefMesh = node.mesh;
      reliefGeom = node.mesh.geometry;
      reliefField = field;
    }
    return reliefRadiusM;
  }

  /**
   * The radius everything geometric agrees on, metres.
   *
   * The pyramid's leaves are built on the manifest's `datum_radius_m` and
   * `bodies.json` carries a mean radius 608 m under it on Mars, so an anchor,
   * a band edge or a pitch floor on the registry's number is that far under the
   * ground the player can see. Read live rather than cached: the manifest lands
   * after the first double-click has already set a lat/lon, and the anchor is
   * rebuilt from that lat/lon on every `solve()`, so the value arriving is all
   * the recompute there is. With no pyramid open it is the radius the globe is
   * drawn at, which a shape globe already takes from the same manifest.
   */
  function datumRadiusM(id: string | null): number | null {
    const row = deps.bodyReaders.rowById(id);
    if (!row) return null;
    if (deps.injects.state.legacyGeometry) return row.radius_m;
    const manifestM = id === deps.pyramidView.tileBody
      ? deps.pyramidView.tileSource?.manifest()?.datum_radius_m ?? null : null;
    return manifestM ?? deps.bodyScene.state.orrery?.byId(row.id)?.drawRadiusM ?? row.radius_m;
  }

  /**
   * The same radius, with the registry as the floor rather than null.
   *
   * `ellipsoid.a_m` is a statement about a body's departure from a sphere, not
   * a sphere anything is drawn on: it stands a tenth of a radius outside the
   * drawn ground on Vesta and Pallas and a quarter outside it on Psyche.
   */
  function groundDatumM(id: string | null): number {
    return datumRadiusM(id) ?? deps.bodyReaders.registryDatumM(id);
  }

  /**
   * The radius the drawn LEAVES are built on, metres.
   *
   * `buildTileMesh` reads the manifest directly, so this is a fact about the
   * meshes on screen and not a derivation: the legacy control moves the camera
   * back onto the registry's radius and must not move the ground with it, or the
   * reading would agree with the fault and report zero error.
   */
  function leafDatumM(): number {
    return deps.pyramidView.tileSource?.manifest()?.datum_radius_m
      ?? groundDatumM(deps.pyramidView.tileBody);
  }

  /** A body-fixed lat/lon as a heliocentric scene-axis position, metres. */
  function surfaceWorld(
    body: OrreryBody, latDeg: number, lonDeg: number, radius = body.row.radius_m,
  ): WorldVec {
    deps._v.copy(latLonToVec3(latDeg, lonDeg, radius)).applyQuaternion(body.quaternion);
    return {
      x: body.world.x + deps._v.x, y: body.world.y + deps._v.y, z: body.world.z + deps._v.z,
    };
  }

  return {
    drawnSurfaceRadiusM,
    restingGroundRadiusM,
    shapeSurfaceRadiusSimM,
    shapeOuterRadiusM,
    reliefSurfaceRadiusM,
    reliefOuterRadiusM,
    cruiseGroundUnderEyeM,
    datumRadiusM,
    groundDatumM,
    leafDatumM,
    surfaceWorld,
    GREY_GROUND_BOUNCE,
  };
}
